import { API_DATE_FORMAT } from "src/consts/global";
import { MAX_ITEMS_PER_PAGE } from "src/components/OrgChart/consts";
import type {
  OrgChartTeamLoadPagination,
  OrgChartTeamLoadResult,
  OrgChartViewOptions,
  Team,
  TeamsPit,
} from "../../types";

/** GraphQL query default cap (see getTeamAndMembersAndChildTeamsQuery). */
export const ORG_CHART_MAX_QUERY_LIMIT = 500;

export type { OrgChartTeamLoadPagination };

/** Shared variables for every org-chart getTeamAndMembersAndChildTeamsQuery refresh. */
export const ORG_CHART_TEAM_LOAD_PAGINATION: OrgChartTeamLoadPagination = {
  fetchMembers: true,
  memberOffset: 0,
  fetchTeams: true,
  teamOffset: 0,
};

export const getLoadedMemberCount = (team: Team | null | undefined): number =>
  team?.directMemberAggregateIds?.length ?? 0;

export const getLoadedChildTeamCount = (
  team: Team | null | undefined
): number => team?.childTeams?.length ?? 0;

/** Pit row for a team id (loaded depth lives on the team, not separate pagination state). */
export const getPitTeamSliceForFetch = (
  teamId: string,
  teamsBaseline: TeamsPit = {},
  teamsDiffline: TeamsPit | null = null
): { baselineTeam: Team | null; difflineTeam: Team | null } => ({
  baselineTeam: teamsBaseline[teamId] ?? null,
  difflineTeam: teamsDiffline?.[teamId] ?? null,
});

export type OrgChartPitMergeOptions = {
  isPaginating: boolean;
  fetchTeams: boolean;
  replaceMembers: boolean;
  appendChildTeamPage: boolean;
};

/** Pit merge flags for one loadTeamDataById fetch (baseline + diffline use the same shape). */
export const deriveOrgChartPitMergeOptions = (
  pagination: OrgChartTeamLoadPagination
): OrgChartPitMergeOptions => {
  const isPaginating = pagination.memberOffset > 0 || pagination.teamOffset > 0;

  return {
    isPaginating,
    fetchTeams: Boolean(pagination.fetchTeams),
    replaceMembers: Boolean(pagination.fetchMembers && !isPaginating),
    appendChildTeamPage: Boolean(
      pagination.fetchTeams && pagination.teamOffset > 0
    ),
  };
};

/**
 * First-page / refresh limits: refetch the same depth the user had loaded (min one page).
 * Load-more passes offsets > 0 unchanged.
 */
export const paginationPreservingLoadedDepth = (
  pagination: OrgChartTeamLoadPagination,
  pitTeam: Team | null | undefined
): OrgChartTeamLoadPagination => {
  if (pagination.memberOffset > 0 || pagination.teamOffset > 0) {
    return pagination;
  }

  const membersLoaded = getLoadedMemberCount(pitTeam);
  const childrenLoaded = getLoadedChildTeamCount(pitTeam);

  const next: OrgChartTeamLoadPagination = { ...pagination };
  if (pagination.fetchMembers) {
    next.membersLimit = Math.min(
      Math.max(membersLoaded, MAX_ITEMS_PER_PAGE),
      ORG_CHART_MAX_QUERY_LIMIT
    );
  }
  if (pagination.fetchTeams) {
    next.teamsLimit = Math.min(
      Math.max(childrenLoaded, MAX_ITEMS_PER_PAGE),
      ORG_CHART_MAX_QUERY_LIMIT
    );
  }
  return next;
};

/** Merge two single-team load payloads (same team id). */
const mergeTeamLoadChunks = (
  accumulated: OrgChartTeamLoadResult | null,
  chunk: OrgChartTeamLoadResult | null | undefined,
  { replace = true }: { replace?: boolean } = {}
): OrgChartTeamLoadResult | null => {
  if (!chunk?.teams) {
    return accumulated;
  }
  const teamId = Object.keys(chunk.teams)[0];
  if (!teamId) {
    return accumulated;
  }
  if (!accumulated?.teams?.[teamId]) {
    return chunk;
  }

  const prev = accumulated.teams[teamId]!;
  const next = chunk.teams[teamId]!;
  const mergedIds = replace
    ? (next.directMemberAggregateIds ?? [])
    : [
        ...new Set([
          ...(prev.directMemberAggregateIds || []),
          ...(next.directMemberAggregateIds || []),
        ]),
      ];

  const childTeams = replace
    ? next.childTeams
    : next.childTeams?.length
      ? next.childTeams
      : prev.childTeams;

  const mergedTeam: Team = {
    ...next,
    id: teamId,
    directMemberAggregateIds: mergedIds,
    directMembers: replace
      ? { ...(next.directMembers || {}) }
      : { ...(prev.directMembers || {}), ...(next.directMembers || {}) },
    ...(childTeams !== undefined ? { childTeams } : {}),
  };

  return {
    teams: {
      [teamId]: mergedTeam,
    },
  };
};

/**
 * Load one team, preserving previously loaded member depth (chains when > 500).
 */
export const loadTeamPreservingMemberDepth = async (
  loadFn: (
    teamId: string,
    pagination: OrgChartTeamLoadPagination,
    queryOverrides: Record<string, unknown>
  ) => Promise<OrgChartTeamLoadResult | null | undefined>,
  teamId: string,
  pagination: OrgChartTeamLoadPagination,
  pitTeam: Team | null | undefined,
  queryOverrides: Record<string, unknown> = {}
): Promise<OrgChartTeamLoadResult | null | undefined> => {
  const depth = getLoadedMemberCount(pitTeam);
  if (
    !pagination.fetchMembers ||
    pagination.memberOffset > 0 ||
    depth <= ORG_CHART_MAX_QUERY_LIMIT
  ) {
    const pag = paginationPreservingLoadedDepth(pagination, pitTeam);
    return loadFn(teamId, pag, queryOverrides);
  }

  let merged: OrgChartTeamLoadResult | null = null;
  for (let offset = 0; offset < depth; offset += ORG_CHART_MAX_QUERY_LIMIT) {
    const limit = Math.min(ORG_CHART_MAX_QUERY_LIMIT, depth - offset);
    const chunk = await loadFn(
      teamId,
      {
        ...pagination,
        memberOffset: offset,
        membersLimit: limit,
      },
      queryOverrides
    );
    if (!chunk?.teams?.[teamId]) {
      break;
    }
    merged = mergeTeamLoadChunks(merged, chunk, { replace: offset === 0 });
    const pageSize = chunk.teams[teamId].directMemberAggregateIds?.length ?? 0;
    if (pageSize < limit) {
      break;
    }
  }
  return merged;
};

export type OrgChartTeamFetchContext = {
  showChanges: boolean;
  baselinePitDate: string;
  difflinePitDate: string | null;
  baselineTeam: Team | null;
  difflineTeam: Team | null;
};

/**
 * Pit dates and loaded slices for one team fetch — shared by expand, load-more, and refresh.
 */
export const buildOrgChartTeamFetchContext = ({
  viewOptions = {},
  teamId,
  teamsBaseline = {},
  teamsDiffline = null,
  fallbackBaselineDate,
}: {
  viewOptions?: OrgChartViewOptions;
  teamId: string;
  teamsBaseline?: TeamsPit;
  teamsDiffline?: TeamsPit | null;
  fallbackBaselineDate: string;
}): OrgChartTeamFetchContext => {
  const showChanges = Boolean(viewOptions.showChanges);
  const baselinePitDate = viewOptions.fromDate?.isValid?.()
    ? viewOptions.fromDate.format(API_DATE_FORMAT)
    : fallbackBaselineDate;
  const difflinePitDate = viewOptions.toDate?.isValid?.()
    ? viewOptions.toDate.format(API_DATE_FORMAT)
    : null;

  return {
    showChanges: Boolean(showChanges && baselinePitDate && difflinePitDate),
    baselinePitDate,
    difflinePitDate,
    ...getPitTeamSliceForFetch(teamId, teamsBaseline, teamsDiffline),
  };
};
